import React, { useState } from "react";
import axios from "axios";
import { ShieldCheck, ArrowRight, CheckCircle2 } from "lucide-react";
import { Input } from "./ui/input";
import VideoBackground from "./VideoBackground";
import HeroVideoLoop from "./HeroVideoLoop";
import ShareableTryoutCard from "./ShareableTryoutCard";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { useToast } from "../hooks/use-toast";
import { playingLevels, positions, heroImages } from "../mock";
import { useSettings } from "../context/SettingsContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


const WAIVER_TEXT = `I, the undersigned participant (or parent/legal guardian of the participant if under 18), acknowledge that participation in soccer training, tryouts, matches, and related activities with Atlético South Florida involves inherent risks of physical injury. In consideration of being permitted to participate, I hereby release, waive, and discharge Atlético South Florida, its coaches, staff, and volunteers from any and all liability for injuries, losses, or damages arising from participation, to the fullest extent permitted by law. I confirm the participant is physically fit to participate and consent to emergency medical treatment if required.`;

const emptyForm = {
  playerFirstName: "", playerLastName: "", dob: "", playingLevel: "", position: "",
  guardianName: "", email: "", phone: "", emergencyContact: "", emergencyPhone: "",
  medicalNotes: "", signature: "", agree: false,
};

export default function Registration() {
  const settings = useSettings();
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const { toast } = useToast();

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.agree) {
      toast({ title: "Waiver required", description: "You must accept the liability waiver to register." });
      return;
    }
    if (!form.signature.trim()) {
      toast({ title: "Signature required", description: "Please type your full name as an e-signature." });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/registrations`, form);
      setDone(true);
      toast({ title: "Registration submitted!", description: "Welcome to Atlético South Florida." });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast({ title: "Submission failed", description: typeof msg === "string" ? msg : "Please check your details." });
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <section id="register" className="relative py-24 bg-background">
        <div className="max-w-2xl mx-auto px-5 text-center">
          <div className="w-20 h-20 rounded-full bg-gold/15 border border-gold/40 flex items-center justify-center mx-auto text-gold">
            <CheckCircle2 size={40} />
          </div>
          <h2 className="display-font uppercase text-white text-4xl mt-8">You're in.</h2>
          <p className="text-white/65 font-light mt-4">
            Thanks, {form.playerFirstName}. Your registration & waiver have been received. Our staff
            will reach out at <span className="text-gold">{form.email}</span> with next steps.
          </p>

          <ShareableTryoutCard playerName={`${form.playerFirstName} ${form.playerLastName}`.trim()} />

          <button
            onClick={() => { setForm(emptyForm); setDone(false); }}
            className="mt-8 cond-font uppercase tracking-widest text-sm border border-white/25 text-white px-6 py-3 rounded-sm hover:border-gold hover:text-gold transition-colors"
            data-testid="register-another-btn"
          >
            Register another player
          </button>
        </div>
      </section>
    );
  }

  return (
    <section id="register" className="relative py-24 overflow-hidden">
      <div className="absolute inset-0">
        {(settings?.registrationVideoType || "mp4") === "youtube" ? (
          <HeroVideoLoop
            videoId={settings?.registrationVideoId || ""}
            poster={heroImages.celebrate}
            loops={settings?.registrationLoops || []}
            logoBetween={settings?.registrationLogoBetween === true}
          />
        ) : (
          <VideoBackground
            type={settings?.registrationVideoType || "mp4"}
            source={settings?.registrationVideoId || "/videos/tryout.mp4"}
            poster={heroImages.celebrate}
          />
        )}
        <div className="absolute inset-0 bg-black/35" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/55 via-black/20 to-transparent" />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-5">
        <div className="grid lg:grid-cols-[1fr_1.4fr] gap-12 items-start">
          <div className="lg:sticky lg:top-28">
            <div className="flex items-center gap-3 mb-4">
              <span className="h-[2px] w-10 bg-gold" />
              <span className="section-label text-gold text-xs">Tryouts & Registration</span>
            </div>
            <h2 className="display-font uppercase text-white text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
              Ready to wear <span className="text-gold">the crest?</span>
            </h2>
            <p className="mt-6 text-white/70 font-light text-lg">
              We are looking for dedicated players who want to train hard, compete at a high level,
              and represent Atlético South Florida with pride.
            </p>
            <div className="mt-8 flex items-center gap-3 text-white/60">
              <ShieldCheck className="text-gold" size={22} />
              <span className="font-light text-sm">Secure registration & digital liability waiver included.</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="bg-black/50 backdrop-blur-lg border border-white/15 rounded-xl p-7 sm:p-9 shadow-2xl">
            <h3 className="cond-font uppercase tracking-widest text-gold text-sm mb-1">Player Information</h3>
            <div className="grid sm:grid-cols-2 gap-4 mt-4">
              <Field label="Player First Name" required>
                <Input value={form.playerFirstName} onChange={(e) => set("playerFirstName", e.target.value)} required className="bg-black/40 border-white/20" data-testid="reg-first" />
              </Field>
              <Field label="Player Last Name" required>
                <Input value={form.playerLastName} onChange={(e) => set("playerLastName", e.target.value)} required className="bg-black/40 border-white/20" data-testid="reg-last" />
              </Field>
              <Field label="Date of Birth" required>
                <Input type="date" value={form.dob} onChange={(e) => set("dob", e.target.value)} required className="bg-black/40 border-white/20 text-white" data-testid="reg-dob" />
              </Field>
              <Field label="Current Level" required>
                <Select value={form.playingLevel} onValueChange={(v) => set("playingLevel", v)}>
                  <SelectTrigger className="bg-black/40 border-white/20" data-testid="reg-level-select"><SelectValue placeholder="Select level" /></SelectTrigger>
                  <SelectContent>{playingLevels.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Preferred Position">
                <Select value={form.position} onValueChange={(v) => set("position", v)}>
                  <SelectTrigger className="bg-black/40 border-white/20" data-testid="reg-position-select"><SelectValue placeholder="Select position" /></SelectTrigger>
                  <SelectContent>{positions.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            </div>

            <h3 className="cond-font uppercase tracking-widest text-gold text-sm mb-1 mt-8">Contact Information</h3>
            <div className="grid sm:grid-cols-2 gap-4 mt-4">
              <Field label="Parent / Guardian Name (if under 18)">
                <Input value={form.guardianName} onChange={(e) => set("guardianName", e.target.value)} className="bg-black/40 border-white/20" data-testid="reg-guardian" />
              </Field>
              <Field label="Email" required>
                <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} required className="bg-black/40 border-white/20" data-testid="reg-email" />
              </Field>
              <Field label="Phone" required>
                <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} required className="bg-black/40 border-white/20" data-testid="reg-phone" />
              </Field>
              <Field label="Emergency Contact Name">
                <Input value={form.emergencyContact} onChange={(e) => set("emergencyContact", e.target.value)} className="bg-black/40 border-white/20" data-testid="reg-emerg-name" />
              </Field>
              <Field label="Emergency Contact Phone">
                <Input value={form.emergencyPhone} onChange={(e) => set("emergencyPhone", e.target.value)} className="bg-black/40 border-white/20" data-testid="reg-emerg-phone" />
              </Field>
            </div>

            <div className="mt-6">
              <Field label="Medical Notes / Allergies">
                <Textarea value={form.medicalNotes} onChange={(e) => set("medicalNotes", e.target.value)} rows={2} className="bg-black/40 border-white/20" placeholder="Optional — anything our staff should know" data-testid="reg-notes" />
              </Field>
            </div>

            <h3 className="cond-font uppercase tracking-widest text-gold text-sm mb-3 mt-8">Liability Waiver & Release</h3>
            <div className="bg-black/40 border border-white/15 rounded-md p-4 max-h-40 overflow-y-auto text-white/60 text-sm font-light leading-relaxed">
              {WAIVER_TEXT}
            </div>

            <label className="flex items-start gap-3 mt-5 cursor-pointer">
              <Checkbox checked={form.agree} onCheckedChange={(v) => set("agree", !!v)} className="mt-0.5 border-white/30 data-[state=checked]:bg-gold data-[state=checked]:text-black data-[state=checked]:border-gold" data-testid="reg-waiver" />
              <span className="text-white/70 text-sm font-light">
                I have read and agree to the liability waiver and release on behalf of the participant.
              </span>
            </label>

            <div className="mt-5">
              <Field label="Digital Signature (type full name)" required>
                <Input value={form.signature} onChange={(e) => set("signature", e.target.value)} required placeholder="Full legal name" className="bg-black/40 border-white/20" style={{ fontFamily: "Oswald, sans-serif" }} data-testid="reg-signature" />
              </Field>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="group w-full mt-7 inline-flex items-center justify-center gap-2 bg-gold text-black cond-font uppercase tracking-widest font-semibold px-7 py-4 rounded-sm hover:bg-gold-light transition-colors disabled:opacity-60"
              data-testid="reg-submit-btn"
            >
              {submitting ? "Submitting..." : "Submit Registration"}
              {!submitting && <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <Label className="cond-font uppercase tracking-wide text-xs text-white/60 mb-2 block">
        {label} {required && <span className="text-gold">*</span>}
      </Label>
      {children}
    </div>
  );
}
